import { format } from 'date-fns';
import moment from 'moment-timezone';
import { getCertificationStatus } from './catalogUtils';

/**
 * Formatea una fecha en formato 'dd/MM/yyyy'.
 * @param {string|Date} dateOriginal - Fecha a formatear.
 * @returns {string} - Fecha formateada.
 */
export const formatDate = (dateOriginal) => {
	const date = new Date(dateOriginal);
	return format(date, 'dd/MM/yyyy');
};

/**
 * Formatea una fecha en formato SQL (YYYY-MM-DD HH:mm:ss).
 * @param {Date} date - Objeto de fecha.
 * @returns {string} - Fecha formateada para SQL.
 */
export const formatDateToSQL = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');
	
	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Convierte una fecha a formato "DD/MM/YY"
 * Acepta cadenas tipo "YYYY-MM-DD HH:mm:ss" o un objeto Date.
 *
 * @param {string|Date} inputDate - Fecha como string o Date
 * @returns {string} Fecha formateada como "DD/MM/YY"
 */
export const formatDateToShort = (inputDate) => {
	let year, month, day;

	if (inputDate instanceof Date) {
		day = String(inputDate.getDate()).padStart(2, '0');
		month = String(inputDate.getMonth() + 1).padStart(2, '0');
		year = String(inputDate.getFullYear()).slice(2);
	} else if (typeof inputDate === 'string') {
		[year, month, day] = inputDate.split(" ")[0].split("-");
		year = year.slice(2);
	}

	return `${day}/${month}/${year}`;
}

/**
 * Verifica si la fecha actual está dentro de un rango de fechas (inclusive).
 * @param {string|Date} assignedDate - Fecha de asignación.
 * @param {string|Date} closedDate - Fecha de cierre.
 * @returns {boolean} - Verdadero si está dentro del rango.
 */
export const isWithinDateRange = (assignedDate, closedDate) => {
	const now = moment();
	const start = moment(assignedDate);
	const end = moment(closedDate);

	return now.isBetween(start, end, 'day', '[]');
};

/**
 * Devuelve el texto y clase CSS correspondientes al estado de certificación.
 * @param {number} status - Código del estado.
 * @param {string|Date} assignedDate - Fecha asignada para la certificación.
 * @param {string|Date} closedDate - Fecha de cierre.
 * @returns {{ text: string, className: string }} - Objeto con texto de estado y clase CSS.
 */
export const getStatusClassAndText = (status, assignedDate, closedDate) => {
	if (status === 5) return { text: "ERROR", className: "text-danger fw-bold" };
	if (status === 1) return { text: "EN PROCESO", className: "text-warning fw-bold" };
	if (status === 2) return { text: "FINALIZADO", className: "text-success fw-bold" };
	if (status === 3) return { text: "EVALUADO", className: "text-success fw-bold" };
	if (status === 4) return { text: "PENALIZADO", className: "text-danger fw-bold" };
	if (status === 0 && !isWithinDateRange(assignedDate, closedDate)) {
		return { text: "FUERA DE TIEMPO", className: "text-warning fw-bold" };
	}

	return { text: getCertificationStatus(status), className: "text-600" };
};

/**
 * Convierte un número de mes (1-12) a su nombre en español.
 * @param {number} numero - Número del mes (1-12).
 * @returns {string|null} - Nombre del mes o null si inválido.
 */
export const numberToMonth = (numero) => {
	const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
	return meses[numero - 1] || null;
};


/**
 * @function getTimestamp
 * @description Devuelve un timestamp Unix en segundos (entero).
 * @returns {number} Timestamp (ej: 1711812160)
 */
export const getTimestamp = () => {
	return Math.floor(Date.now() / 1000);
};